import { SchemaFormSchema } from "components/Form/components/SchemaForm/SchemaForm.types";

/**
 * Form-dialect (form-*) schema that drives the no-code egress_policy rule
 * editor (operator-task-egress-policy-ux-improvements-icon-schemaform-scopepicker-markdownlog,
 * item #2). It renders `egress_policy.rules` — a rule list resolved as
 * deny > allow > policy default — as a structured SchemaForm instead of a raw YAML code editor, and
 * reuses the SchemaForm component's built-in Code-view toggle as the raw-edit
 * escape hatch (so a rule shape the form cannot represent is never dropped).
 *
 * A rule is `{ connector_type, match: { field: glob, ... }, effect }`. The
 * `match` fields a rule can set depend on the channel (connector_type), so the
 * rule list is modelled as a DISCRIMINATED UNION: `rules.items` is a
 * `form-dropdown` with `objectIdentifier: "connector_type"`, and each `oneOf`
 * branch is a self-contained `form-object` for one channel whose `match`
 * sub-object carries exactly that channel's destination fields.
 *
 * The destination vocabulary is CLOSED and mirrors the backend extractor map in
 * `cloudomation/workspace/egress_gate.py` (`_EXTRACTORS`):
 *   - smtp: recipient (to/cc/bcc), relay_host (the SMTP server)
 *   - http: host, method (GET/POST/…), path (base_path + path)  [the `rest`
 *     connector-type is a canonical alias of http — store it as http]
 *   - ssh / scp: host
 *   - git: repo_url
 *   - sync: host (remote workspace of an outgoing workspace sync)
 *   - ldap: host (directory server of an LDAP configuration)
 *   - vault: host (server of a Vault configuration)
 *   - devolutions: host (server of a Devolutions configuration)
 *   - bundle_repository: host (remote of a bundle repository)
 *   - `*` (any channel): host, recipient, relay_host, method, path, repo_url —
 *     a rule_ct of `*` applies to every channel, but only the destination
 *     fields a given connection actually exposes can match.
 * Keep this in sync with egress_gate.py if the vocabulary changes. Any match
 * key the backend does not extract for a channel simply never matches (a typo
 * fails safe — it does not silently allow).
 *
 * The backend evaluation (`egress_gate.evaluate_rules`) is defensive — it skips
 * a malformed rule and treats an unrecognised effect as no-match — so this
 * editor-facing schema does not enforce correctness server-side.
 */

// Glob-help shared by every match field. Mirrors the negation/escape semantics
// documented in egress_gate.py (`_rule_matches`).
const GLOB_HELP =
    "A case-insensitive glob (`*`, `?`, `[…]`), e.g. `*@cloudomation.com` or `*.ovh.net`. " +
    "Prefix with `!` to negate (match when a destination value does NOT fit the glob — " +
    "used to deny a send as soon as one destination falls outside an allowlist). " +
    "Escape a literal leading `!` as `\\!`. Leave empty to not constrain this field.";

const globField = (label: string, description?: string) => ({
    element: "form-optional-string",
    type: "string",
    label,
    description: description ? `${description} ${GLOB_HELP}` : GLOB_HELP,
});

// --- Reusable match-field atoms (per the closed backend vocabulary) --------
const recipientField = globField("Recipient", "Any mail recipient (to, cc or bcc).");
const relayHostField = globField("Relay host", "The SMTP server the mail is relayed through.");
const hostField = globField("Host", "The destination host.");
const methodField = globField("Method", "The HTTP method, e.g. `GET`, `POST`, `*` for any.");
const pathField = globField("Path", "The request path (base path + path), e.g. `/api/*`.");
const repoUrlField = globField("Repository URL", "The git repository URL.");

const effectField = {
    element: "form-dropdown",
    label: "Effect",
    description:
        "What this rule does when it matches. A matching Deny rule always wins over a matching " +
        "Allow rule, so the order of the rules does not matter. If no rule matches, the " +
        "policy's default applies.",
    defaultIndex: 0,
    anyOf: [
        { element: "form-const-string", type: "string", const: "allow", label: "Allow" },
        { element: "form-const-string", type: "string", const: "deny", label: "Deny" },
    ],
};

/**
 * Build one discriminated-union branch (a `form-object` for a single channel).
 * The `connector_type` property is a label-less `form-const-string` so it acts
 * as the dropdown discriminant (objectIdentifier: "connector_type") and is
 * tucked into the collapsed "More…" section rather than shown as a greyed-out
 * row (the dropdown already names the channel). `matchProperties` is the closed
 * set of destination fields for that channel; an empty set (none) renders a
 * channel-wide rule (matches every connection of that type).
 */
const ruleBranch = (
    connectorType: string,
    label: string,
    matchProperties: Record<string, unknown>,
    matchDescription: string,
) => ({
    element: "form-object",
    type: "object",
    additionalProperties: false,
    label,
    required: ["connector_type"],
    order: ["effect", "match"],
    hidden: ["connector_type"],
    properties: {
        connector_type: { element: "form-const-string", type: "string", const: connectorType },
        effect: effectField,
        match: {
            element: "form-object",
            type: "object",
            additionalProperties: false,
            label: "Match",
            description: matchDescription,
            properties: matchProperties,
        },
    },
});

const MATCH_AND_HELP =
    "All set fields must match (AND). Leave every field empty to match every connection of this channel.";

const ruleBranches = [
    ruleBranch(
        "smtp",
        "SMTP (email)",
        { recipient: recipientField, relay_host: relayHostField },
        `Outbound email. ${MATCH_AND_HELP}`,
    ),
    ruleBranch(
        "http",
        "HTTP / REST",
        { host: hostField, method: methodField, path: pathField },
        `HTTP(S) requests (the \`rest\` connector type is stored as \`http\`). ${MATCH_AND_HELP}`,
    ),
    ruleBranch("ssh", "SSH", { host: hostField }, `SSH connections. ${MATCH_AND_HELP}`),
    ruleBranch("scp", "SCP (file transfer)", { host: hostField }, `SCP file transfers. ${MATCH_AND_HELP}`),
    ruleBranch("git", "Git", { repo_url: repoUrlField }, `Git operations. ${MATCH_AND_HELP}`),
    ruleBranch(
        "sync",
        "Workspace sync",
        { host: hostField },
        `Outgoing workspace-sync connections to a remote workspace (host of the sync configuration's remote URL). ${MATCH_AND_HELP}`,
    ),
    ruleBranch(
        "ldap",
        "LDAP",
        { host: hostField },
        `LDAP directory connections (logins and connection tests; host of the LDAP configuration's server URL). ${MATCH_AND_HELP}`,
    ),
    ruleBranch(
        "vault",
        "Vault",
        { host: hostField },
        `Requests to a Vault server (secret references, reading and writing secrets, connection tests; host of the Vault configuration's URL). ${MATCH_AND_HELP}`,
    ),
    ruleBranch(
        "devolutions",
        "Devolutions",
        { host: hostField },
        `Requests to a Devolutions Server (secret references and connection tests; host of the Devolutions configuration's URL). ${MATCH_AND_HELP}`,
    ),
    ruleBranch(
        "bundle_repository",
        "Bundle repository",
        { host: hostField },
        `Requests to a bundle repository (listing, installing, releasing and deleting bundles; host of the repository's remote URL). ${MATCH_AND_HELP}`,
    ),
    ruleBranch(
        "*",
        "Any channel",
        {
            host: hostField,
            recipient: recipientField,
            relay_host: relayHostField,
            method: methodField,
            path: pathField,
            repo_url: repoUrlField,
        },
        "Applies to every channel. Only the destination fields a given connection actually " +
            `exposes can match. ${MATCH_AND_HELP}`,
    ),
];

export const egressPolicyRulesSchema: SchemaFormSchema = {
    element: "form-object",
    type: "object",
    additionalProperties: false,
    style: { border: false, align: "VERTICAL" },
    properties: {
        rules: {
            element: "form-array",
            type: "array",
            label: "Rules",
            itemsLabel: "rule",
            // Deleting a rule via the array item's 'x' discards its whole
            // config; require an "are you sure?" confirmation first.
            confirmDelete: true,
            items: {
                element: "form-dropdown",
                label: "Channel",
                objectIdentifier: "connector_type",
                defaultIndex: 0,
                oneOf: ruleBranches,
            },
        },
    },
};

export default egressPolicyRulesSchema;
